/**
 * App Config — enterprise vs self-service flavour (RFC-0503 Alt 3, step 1)
 *
 * Centralizes the handful of ways the enterprise build diverges from the
 * self-service build (auth methods, storage namespace, branding, payment
 * surface) behind a single injected config object, so downstream code branches
 * on `appConfig.*` instead of re-deriving the flavour ad hoc.
 *
 * The flavour is chosen at build time from `import.meta.env.VITE_FLAVOUR`,
 * with a runtime override from `window.__APP_CONFIG__.ACCOUNT_TYPE` (the
 * container entrypoint generates config.js from environment variables).
 * Runtime wins over build time; when neither is set we default to
 * self-service.
 */

export type AccountType = 'selfserve' | 'enterprise';

export interface AppConfig {
  /** Base URL the SPA is served from (mirrors `import.meta.env.BASE_URL`). */
  basePath: string;
  /** Which product flavour this build/runtime is acting as. */
  accountType: AccountType;
  /** Enabled sign-in methods for this flavour. */
  login: {
    magicLink: boolean;
    sso: boolean;
  };
  /** Prefix for keys this flavour writes to storage, so an enterprise and a
   *  self-service SPA on the same origin don't collide. */
  storageNamespace: string;
  /** Self-service payment surface (Stripe checkout / buy-credits / referral /
   *  low-balance top-up prompts). Disabled for enterprise — credit comes only
   *  from the firm org pool, admin-set, never a self-service purchase
   *  (RFC-0501 "No Payment for Enterprise"; RFC-0503 divergence registry). */
  payments: {
    enabled: boolean;
  };
  /** Per-user credit *visibility*. Distinct from `payments` — a flavour can
   *  have no purchase path yet still meter per-user credits, or vice versa.
   *  Enterprise runs on a shared firm pool (RFC-0501), so an individual
   *  balance/cost is noise there; the pool and per-user usage live in the Team
   *  section for org_admins instead. Every surface that renders a per-user
   *  balance, a per-analysis credit cost, or credit-grant copy must branch on
   *  this — never on `payments.enabled`, and never on `accountType` directly. */
  credits: {
    /** Show the header credits pill and per-analysis cost/balance copy. */
    showIndividualBalance: boolean;
    /** Show credit-grant onboarding copy (signup bonus, corporate-email bonus,
     *  referral credits) — self-service acquisition mechanics only. */
    showGrantCopy: boolean;
  };
  /** Left-hand navigation.
   *
   *  ⚠️ A FLAVOUR DIFFERENCE, NOT A FEATURE FLAG. The two products need
   *  different amounts of navigation because they hold different amounts of
   *  product, and that is permanent — not a rollout we intend to converge.
   *
   *  SELF-SERVICE has none. One user, their own analyses, and a create button
   *  already on the page; a persistent rail whose entries duplicate what the
   *  page shows is chrome that costs a column and earns nothing. This follows
   *  the dashboard's lead on `develop` (`2cbce258`), which dropped its left nav
   *  on exactly that reasoning and withheld the mobile hamburger with it —
   *  a hamburger that opens nothing is worse than no hamburger.
   *
   *  ENTERPRISE keeps it. A firm has surfaces a single user does not — other
   *  people's cases, the analyses over them, and the user administration behind
   *  them — and none of those is reachable from the others' pages. */
  leftNav: {
    /** Render the left-hand `AppSidebar` (and, with it, the mobile hamburger
     *  that opens it). When false, NOTHING should render a menu control:
     *  see `AppLayout`. */
    enabled: boolean;
  };
  /** The Workspace — the case browser and the two-pane case surface
   *  (RFC-0206 Surface 1).
   *
   *  ⚠️ ENTERPRISE ONLY, by product decision (RFC-0206 D6, answered
   *  2026-08-19). A self-service account is one person working through their
   *  own analyses: the dashboard already lists everything they have, so a
   *  second top-level surface that groups those analyses by case is a
   *  navigation layer over a set small enough not to need one. A firm is the
   *  opposite — many people, many matters, and the case IS the unit colleagues
   *  hand to each other.
   *
   *  This is why self-service has no left nav (see `leftNav`): with the
   *  workspace absent there is no second destination to navigate to. The two
   *  settings are separate keys because they are separate questions — one is
   *  "does this surface exist", the other "does this build need a rail" — but
   *  today they answer the same way, and turning the workspace on for
   *  self-service without also turning on a way to reach it would ship a page
   *  with no entrance. */
  workspace: {
    enabled: boolean;
  };
  /** User-facing product identity. */
  branding: {
    productName: string;
  };
}

/** Per-flavour values, sans `basePath` (which is shared, from BASE_URL). */
const FLAVOURS: Record<AccountType, Omit<AppConfig, 'basePath'>> = {
  selfserve: {
    accountType: 'selfserve',
    login: { magicLink: true, sso: true },
    storageNamespace: 'attain_',
    payments: { enabled: true },
    credits: { showIndividualBalance: true, showGrantCopy: true },
    leftNav: { enabled: false },
    workspace: { enabled: false },
    branding: { productName: 'Attain' },
  },
  enterprise: {
    accountType: 'enterprise',
    login: { magicLink: false, sso: true },
    storageNamespace: 'attain_enterprise_',
    payments: { enabled: false },
    credits: { showIndividualBalance: false, showGrantCopy: false },
    leftNav: { enabled: true },
    workspace: { enabled: true },
    branding: { productName: 'Attain Business' },
  },
};

const isAccountType = (value: unknown): value is AccountType =>
  value === 'selfserve' || value === 'enterprise';

/**
 * Resolve the active flavour: runtime override first, then the build-time
 * flavour, then the self-service default.
 */
function resolveAccountType(): AccountType {
  // Runtime override — read through a narrow cast, matching the sibling
  // config modules (see config/api.ts, config/features.ts).
  const runtime = ((window.__APP_CONFIG__ ?? {}) as { ACCOUNT_TYPE?: unknown })
    .ACCOUNT_TYPE;
  if (isAccountType(runtime)) return runtime;

  // Build-time flavour (baked in by Vite via `VITE_FLAVOUR`).
  const buildTime = import.meta.env.VITE_FLAVOUR;
  if (isAccountType(buildTime)) return buildTime;

  return 'selfserve';
}

export const appConfig: AppConfig = {
  basePath: import.meta.env.BASE_URL,
  ...FLAVOURS[resolveAccountType()],
};
